import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from 'services/api';
import useAuthStore from 'stores/authStore';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Badge } from 'components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from 'components/ui/dialog';
import { 
  ArrowLeft, UserPlus, Mail, Phone, DollarSign, 
  Clock, CheckCircle, XCircle, Send, Users, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

export default function QuickEnroll() {
  const navigate = useNavigate();
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    caregiver_name: '',
    caregiver_email: '',
    caregiver_phone: '',
    relationship: 'hired',
    hourly_rate: ''
  });

  useEffect(() => {
    fetchEnrollments();
  }, []);

  const fetchEnrollments = async () => {
    try {
      const response = await api.get('/caregivers/enrollments');
      setEnrollments(response.data.enrollments);
    } catch (error) {
      console.error('Failed to fetch enrollments:', error);
    } finally {
      setLoading(false);
    }
  };

  const [phoneError, setPhoneError] = useState('');

  const validatePhone = (phone) => {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 10) return 'Phone number must be at least 10 digits';
    if (digits.length > 15) return 'Phone number is too long';
    return '';
  };

  const handlePhoneChange = (value) => {
    setFormData({...formData, caregiver_phone: value});
    setPhoneError(validatePhone(value));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const phoneErr = validatePhone(formData.caregiver_phone);
    if (phoneErr) {
      setPhoneError(phoneErr);
      return;
    }
    setSubmitting(true);

    try {
      const payload = {
        name: formData.caregiver_name,
        email: formData.caregiver_email,
        phone: formData.caregiver_phone,
        relationship: formData.relationship,
        hourly_rate: formData.hourly_rate ? parseFloat(formData.hourly_rate) : null
      };
      
      const response = await api.post('/caregivers/quick-enroll', payload);
      
      if (response.data.email_sent) {
        toast.success('Invitation sent successfully!');
      } else {
        toast.info('Enrollment created. Email notification failed.');
      }
      
      setDialogOpen(false);
      setFormData({
        caregiver_name: '',
        caregiver_email: '',
        caregiver_phone: '',
        relationship: 'hired',
        hourly_rate: ''
      });
      fetchEnrollments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      case 'accepted':
        return <Badge className="bg-teal-100 text-teal-700"><CheckCircle className="w-3 h-3 mr-1" />Accepted</Badge>;
      case 'declined':
        return <Badge className="bg-rose-100 text-rose-700"><XCircle className="w-3 h-3 mr-1" />Declined</Badge>;
      case 'expired':
        return <Badge className="bg-slate-100 text-slate-700">Expired</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getRelationshipLabel = (relationship) => {
    const labels = {
      hired: 'Hired Caregiver',
      family: 'Family Member',
      volunteer: 'Volunteer',
      agency: 'Agency Worker'
    };
    return labels[relationship] || relationship;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-slate-900 font-['Manrope']" data-testid="quick-enroll-title">Quick Enroll</h1>
              <p className="text-xs sm:text-sm text-slate-500">Invite and manage your care team</p>
            </div>
          </div>
          <Button className="bg-teal-500 hover:bg-teal-600 text-white shrink-0" size="sm" data-testid="invite-caregiver-btn" onClick={() => setDialogOpen(true)}>
            <UserPlus className="w-4 h-4" />
            <span className="hidden sm:inline">Invite</span> Caregiver
          </Button>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-['Manrope']">Invite a Caregiver</DialogTitle>
                  <DialogDescription>Send an invitation to join your care team</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Caregiver Name *</Label>
                    <Input
                      id="name"
                      placeholder="Jane Smith"
                      value={formData.caregiver_name}
                      onChange={(e) => setFormData({...formData, caregiver_name: e.target.value})}
                      required
                      data-testid="enroll-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="jane@example.com"
                      value={formData.caregiver_email}
                      onChange={(e) => setFormData({...formData, caregiver_email: e.target.value})}
                      required
                      data-testid="enroll-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="(555) 123-4567"
                      value={formData.caregiver_phone}
                      onChange={(e) => handlePhoneChange(e.target.value)}
                      className={phoneError ? 'border-rose-500' : ''}
                      data-testid="enroll-phone-input"
                    />
                    {phoneError && <p className="text-xs text-rose-600">{phoneError}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label>Relationship</Label>
                    <Select 
                      value={formData.relationship} 
                      onValueChange={(v) => setFormData({...formData, relationship: v})}
                    >
                      <SelectTrigger data-testid="enroll-relationship-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hired">Hired Caregiver</SelectItem>
                        <SelectItem value="family">Family Member</SelectItem>
                        <SelectItem value="volunteer">Volunteer</SelectItem>
                        <SelectItem value="agency">Agency Worker</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="rate">Hourly Rate ($)</Label>
                    <Input
                      id="rate"
                      type="number"
                      step="0.01"
                      placeholder="15.00"
                      value={formData.hourly_rate}
                      onChange={(e) => setFormData({...formData, hourly_rate: e.target.value})}
                      data-testid="enroll-rate-input"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-teal-500 hover:bg-teal-600" 
                    disabled={submitting}
                    data-testid="send-invitation-btn"
                  >
                    {submitting ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                    Send Invitation
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
        </div>
      </header>

      <div className="max-w-4xl mx-auto p-6">
        {/* Enrollment List */}
        <Card>
          <CardHeader>
            <CardTitle className="font-['Manrope'] flex items-center gap-2">
              <Users className="w-5 h-5 text-teal-500" />
              Invited Caregivers
            </CardTitle>
          </CardHeader>
          <CardContent>
            {enrollments.length === 0 ? (
              <div className="text-center py-12">
                <UserPlus className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">No Caregivers Invited Yet</h3>
                <p className="text-slate-500 mb-4">Invite caregivers to join your care team</p>
                <Button onClick={() => setDialogOpen(true)} className="bg-teal-500 hover:bg-teal-600">
                  <UserPlus className="w-4 h-4" />
                  Invite Your First Caregiver
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {enrollments.map((enrollment) => (
                  <div
                    key={enrollment.enrollment_id}
                    className="flex items-start justify-between p-3 sm:p-4 bg-slate-50 rounded-xl gap-2"
                    data-testid={`enrollment-${enrollment.enrollment_id}`}
                  >
                    <div className="flex items-start gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center shrink-0">
                        <span className="text-teal-600 font-semibold">
                          {enrollment.caregiver_name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <h4 className="font-medium text-sm sm:text-base">{enrollment.caregiver_name}</h4>
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs sm:text-sm text-slate-500">
                          <span className="flex items-center gap-1 min-w-0">
                            <Mail className="w-3 h-3 shrink-0" />
                            <span className="truncate">{enrollment.caregiver_email}</span>
                          </span>
                          {enrollment.caregiver_phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3 shrink-0" />
                              {enrollment.caregiver_phone}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">
                            {getRelationshipLabel(enrollment.relationship)}
                          </Badge>
                          {enrollment.hourly_rate && (
                            <Badge variant="outline" className="text-xs">
                              <DollarSign className="w-3 h-3" />
                              {enrollment.hourly_rate}/hr
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      {getStatusBadge(enrollment.status)}
                      <p className="text-xs text-slate-400 mt-1">
                        {new Date(enrollment.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
